import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { Link } from "react-router-dom";
import { createAPIKey, deleteAPIKey, getAPIKeys, getCapabilities, getMySettings, updateMySettings } from "@/api/endpoints";
import { useAuth } from "@/hooks/useAuth";
import { useApi } from "@/hooks/useApi";

type SettingsSection = "account" | "apiKeys" | "playback" | "about";

type StatusState = {
  kind: "success" | "warning" | "error";
  message: string;
};

export function SettingsPage() {
  const { user } = useAuth();
  const caps = useApi(() => getCapabilities(), []);
  const apiKeys = useApi(() => getAPIKeys(), []);
  const mySettings = useApi(() => getMySettings(), []);
  const [section, setSection] = useState<SettingsSection>("account");
  const [newKeyName, setNewKeyName] = useState("");
  const [creatingKey, setCreatingKey] = useState(false);
  const [revokingKeyID, setRevokingKeyID] = useState<string | null>(null);
  const [preferredAudioLanguage, setPreferredAudioLanguage] = useState("");
  const [preferredSubtitleLanguage, setPreferredSubtitleLanguage] = useState("");
  const [savingPlayback, setSavingPlayback] = useState(false);
  const [revealedToken, setRevealedToken] = useState<string | null>(null);
  const [status, setStatus] = useState<StatusState | null>(null);

  useEffect(() => {
    const attrs = mySettings.data?.data.attributes;
    if (!attrs) {
      return;
    }
    setPreferredAudioLanguage(attrs.preferred_audio_language ?? "");
    setPreferredSubtitleLanguage(attrs.preferred_subtitle_language ?? "");
  }, [mySettings.data]);

  async function handleCreateAPIKey(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setCreatingKey(true);
    setStatus(null);
    try {
      const response = await createAPIKey({ name: newKeyName.trim() || undefined });
      setNewKeyName("");
      setRevealedToken(response.data.attributes.token ?? null);
      setStatus({
        kind: "success",
        message: "API key created. Copy it now. The full value is only shown once.",
      });
      await apiKeys.refetch();
    } catch (error) {
      setStatus({
        kind: "error",
        message: error instanceof Error ? error.message : "Failed to create API key.",
      });
    } finally {
      setCreatingKey(false);
    }
  }

  async function handleRevokeAPIKey(keyID: string, keyName: string) {
    if (!window.confirm(`Revoke API key "${keyName}"?`)) {
      return;
    }
    setRevokingKeyID(keyID);
    setStatus(null);
    try {
      await deleteAPIKey(keyID);
      if (revealedToken) {
        setRevealedToken(null);
      }
      setStatus({
        kind: "success",
        message: "API key revoked.",
      });
      await apiKeys.refetch();
    } catch (error) {
      setStatus({
        kind: "error",
        message: error instanceof Error ? error.message : "Failed to revoke API key.",
      });
    } finally {
      setRevokingKeyID(null);
    }
  }

  async function handleCopyRevealedToken() {
    if (!revealedToken) {
      return;
    }
    try {
      await navigator.clipboard.writeText(revealedToken);
      setStatus({
        kind: "success",
        message: "API key copied to the clipboard.",
      });
    } catch {
      setStatus({
        kind: "warning",
        message: "Clipboard copy failed. Copy the key manually before leaving this page.",
      });
    }
  }

  async function handleSavePlaybackSettings(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSavingPlayback(true);
    setStatus(null);
    try {
      await updateMySettings({
        preferred_audio_language: preferredAudioLanguage.trim(),
        preferred_subtitle_language: preferredSubtitleLanguage.trim(),
      });
      setStatus({
        kind: "success",
        message: "Playback preferences saved.",
      });
      await mySettings.refetch();
    } catch (error) {
      setStatus({
        kind: "error",
        message: error instanceof Error ? error.message : "Failed to save playback preferences.",
      });
    } finally {
      setSavingPlayback(false);
    }
  }

  return (
    <div className="settings-page">
      <div className="settings-header">
        <h1>Settings</h1>
        <p className="settings-copy">
          Personal settings follow the Plex pattern: account and client behavior here, server administration separately.
        </p>
      </div>

      <div className="settings-shell">
        <aside className="settings-nav">
          <div className="settings-nav-group">
            <div className="settings-nav-label">Settings</div>
            <button
              type="button"
              className={`settings-nav-item${section === "account" ? " settings-nav-item--active" : ""}`}
              onClick={() => setSection("account")}
            >
              Account
            </button>
            <button
              type="button"
              className={`settings-nav-item${section === "apiKeys" ? " settings-nav-item--active" : ""}`}
              onClick={() => setSection("apiKeys")}
            >
              API Keys
            </button>
            <button
              type="button"
              className={`settings-nav-item${section === "playback" ? " settings-nav-item--active" : ""}`}
              onClick={() => setSection("playback")}
            >
              Player
            </button>
            <button
              type="button"
              className={`settings-nav-item${section === "about" ? " settings-nav-item--active" : ""}`}
              onClick={() => setSection("about")}
            >
              About
            </button>
          </div>
        </aside>

        <div className="settings-panel">
          {status && (
            <div className={`settings-status settings-status--${status.kind}`}>
              {status.message}
            </div>
          )}

          {section === "account" && (
            <section className="settings-section">
              <div className="settings-section-header">
                <div>
                  <h2>Account</h2>
                  <p className="settings-note">Identity and role for the currently signed-in user.</p>
                </div>
              </div>
              <div className="settings-row">
                <span className="settings-label">Username</span>
                <span className="settings-value">{user?.attributes.username}</span>
              </div>
              <div className="settings-row">
                <span className="settings-label">Display Name</span>
                <span className="settings-value">{user?.attributes.display_name}</span>
              </div>
              <div className="settings-row">
                <span className="settings-label">Role</span>
                <span className="settings-value">
                  {user?.attributes.is_admin ? "Administrator" : "User"}
                </span>
              </div>
              {user?.attributes.is_admin && (
                <p className="settings-help">
                  Administrative server controls live under <Link to="/admin/settings">Server Settings</Link>.
                </p>
              )}
            </section>
          )}

          {section === "apiKeys" && (
            <section className="settings-section">
              <div className="settings-section-header">
                <div>
                  <h2>API Keys</h2>
                  <p className="settings-note">
                    Create long-lived keys for scripts and external clients. Each key inherits your current KnotFin permissions.
                  </p>
                </div>
                <span className="badge badge--muted">
                  {apiKeys.data?.data.length ?? 0} saved
                </span>
              </div>

              <form className="settings-form settings-form--stacked" onSubmit={(event) => void handleCreateAPIKey(event)}>
                <div className="settings-grid settings-grid--api-key">
                  <label className="settings-field">
                    <span>New Key Name</span>
                    <input
                      className="settings-input"
                      value={newKeyName}
                      onChange={(event) => setNewKeyName(event.target.value)}
                      placeholder="CI Agent"
                    />
                  </label>
                  <div className="settings-actions settings-actions--end">
                    <button className="btn btn--primary" type="submit" disabled={creatingKey}>
                      {creatingKey ? "Creating..." : "Create Key"}
                    </button>
                  </div>
                </div>
              </form>

              <p className="settings-help">
                Use keys with <code>Authorization: Bearer &lt;api-key&gt;</code>. Keys are shown once at creation, can be revoked at any time, and stop working immediately after revocation.
              </p>

              {revealedToken && (
                <div className="settings-token-panel">
                  <label className="settings-field">
                    <span>Copy This Key Now</span>
                    <textarea
                      className="settings-input settings-token-display"
                      readOnly
                      rows={3}
                      value={revealedToken}
                    />
                  </label>
                  <div className="settings-actions">
                    <button className="btn btn--secondary" type="button" onClick={() => void handleCopyRevealedToken()}>
                      Copy Key
                    </button>
                  </div>
                </div>
              )}

              {apiKeys.loading && <p>Loading...</p>}
              {!apiKeys.loading && apiKeys.data && apiKeys.data.data.length === 0 && (
                <p className="settings-help">No API keys yet.</p>
              )}
              {!apiKeys.loading && apiKeys.data && apiKeys.data.data.length > 0 && (
                <div className="settings-key-list">
                  {apiKeys.data.data.map((key) => (
                    <div key={key.id} className="settings-key-item">
                      <div className="settings-key-meta">
                        <div className="settings-library-title-row">
                          <strong>{key.attributes.name}</strong>
                          <span className="badge badge--muted">{key.id}</span>
                        </div>
                        <div className="settings-key-detail">
                          Created {formatTimestamp(key.attributes.created_at)}
                        </div>
                        <div className="settings-key-detail">
                          Last used {formatTimestamp(key.attributes.last_used_at ?? null)}
                        </div>
                      </div>
                      <div className="settings-library-actions">
                        <button
                          className="btn btn--secondary"
                          type="button"
                          disabled={revokingKeyID === key.id}
                          onClick={() => void handleRevokeAPIKey(key.id, key.attributes.name)}
                        >
                          {revokingKeyID === key.id ? "Revoking..." : "Revoke"}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}

          {section === "playback" && (
            <section className="settings-section">
              <div className="settings-section-header">
                <div>
                  <h2>Player</h2>
                  <p className="settings-note">Client playback follows the server’s adaptive streaming policy and your language preferences.</p>
                </div>
              </div>
              <form className="settings-form settings-form--stacked" onSubmit={(event) => void handleSavePlaybackSettings(event)}>
                <div className="settings-grid">
                  <label className="settings-field">
                    <span>Preferred Audio Language</span>
                    <input
                      className="settings-input"
                      type="text"
                      value={preferredAudioLanguage}
                      onChange={(event) => setPreferredAudioLanguage(event.target.value)}
                      placeholder="en"
                    />
                  </label>
                  <label className="settings-field">
                    <span>Preferred Subtitle Language</span>
                    <input
                      className="settings-input"
                      type="text"
                      value={preferredSubtitleLanguage}
                      onChange={(event) => setPreferredSubtitleLanguage(event.target.value)}
                      placeholder="en"
                    />
                  </label>
                </div>
                <div className="settings-actions">
                  <button className="btn btn--primary" type="submit" disabled={savingPlayback}>
                    {savingPlayback ? "Saving..." : "Save Playback Preferences"}
                  </button>
                </div>
              </form>
              <div className="settings-row">
                <span className="settings-label">Streaming Mode</span>
                <span className="settings-value">Automatic</span>
              </div>
              <div className="settings-row">
                <span className="settings-label">Remote Reliability Bias</span>
                <span className="settings-value">Conservative</span>
              </div>
              <p className="settings-help">
                KnotFin currently prioritizes stable playback over peak quality, which matches the original remote streaming goal.
              </p>
            </section>
          )}

          {section === "about" && (
            <section className="settings-section">
              <div className="settings-section-header">
                <div>
                  <h2>About</h2>
                  <p className="settings-note">Current server and API capability summary for this client.</p>
                </div>
              </div>
              {caps.loading && <p>Loading...</p>}
              {caps.data && (() => {
                const a = caps.data.data.attributes;
                return (
                  <>
                    <div className="settings-row">
                      <span className="settings-label">Server Version</span>
                      <span className="settings-value">{a.server_version}</span>
                    </div>
                    <div className="settings-row">
                      <span className="settings-label">API Version</span>
                      <span className="settings-value">{a.api_version}</span>
                    </div>
                    <div className="settings-row">
                      <span className="settings-label">Authentication</span>
                      <span className="settings-value">{a.auth_methods.join(", ")}</span>
                    </div>
                    <div className="settings-row">
                      <span className="settings-label">Playback Protocols</span>
                      <span className="settings-value">{a.playback_protocols.join(", ")}</span>
                    </div>
                    <div className="settings-row">
                      <span className="settings-label">Library Types</span>
                      <span className="settings-value">{a.library_types.join(", ")}</span>
                    </div>
                  </>
                );
              })()}
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

function formatTimestamp(value: string | null | undefined): string {
  if (!value) {
    return "never";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}
